import React from 'react';
import { useTranslation } from 'react-i18next';
import { GraduationCap, Briefcase, Users } from 'lucide-react';

const WhoBenefits = () => {
  const { t } = useTranslation();
  
  const audiences = [
    {
      icon: GraduationCap,
      title: t('whoBenefits.students.title', 'Students'),
      description: t('whoBenefits.students.description', 'Perfect for recording lectures, taking comprehensive notes, and organizing study materials efficiently.'),
      benefits: Array.isArray(t('whoBenefits.students.benefits', { returnObjects: true })) 
        ? t('whoBenefits.students.benefits', { returnObjects: true }) as string[]
        : [
            "Record lectures with instant transcription",
            "Organize notes by subject and topic", 
            "Generate smart summaries for quick review",
            "Search through all your study materials"
          ]
    },
    {
      icon: Briefcase,
      title: t('whoBenefits.professionals.title', 'Professionals'),
      description: t('whoBenefits.professionals.description', 'Streamline your workflow with AI-powered meeting notes, brainstorming sessions, and project documentation.'),
      benefits: Array.isArray(t('whoBenefits.professionals.benefits', { returnObjects: true })) 
        ? t('whoBenefits.professionals.benefits', { returnObjects: true }) as string[]
        : [
            "Capture meeting minutes automatically",
            "Document brainstorming sessions",
            "Organize project details and action items", 
            "Share formatted reports instantly"
          ]
    },
    {
      icon: Users,
      title: t('whoBenefits.everyone.title', 'Everyone'),
      description: t('whoBenefits.everyone.description', 'A versatile platform for anyone who needs to capture, organize, and manage information effectively.'),
      benefits: Array.isArray(t('whoBenefits.everyone.benefits', { returnObjects: true })) 
        ? t('whoBenefits.everyone.benefits', { returnObjects: true }) as string[]
        : [
            "Personal voice memos and reminders",
            "Interview documentation", 
            "Creative idea capture",
            "Multi-device synchronization"
          ]
    }
  ];

  return (
    <section className="py-20 px-4 bg-gray-50 dark:bg-slate-800">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 dark:text-white">
            {t('whoBenefits.title')}
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto dark:text-gray-400">
            {t('whoBenefits.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {audiences.map((audience, index) => {
            const Icon = audience.icon;
            return (
              <div
                key={index}
                className="bg-white border border-gray-200 shadow-sm rounded-xl p-6 hover:shadow-md transition-all duration-300 dark:bg-slate-700 dark:border-slate-600"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-primary/20 p-3 rounded-lg">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{audience.title}</h3>
                </div>

                <p className="text-gray-600 mb-6 leading-relaxed dark:text-gray-300">
                  {audience.description}
                </p>

                <ul className="space-y-2">
                  {audience.benefits.map((benefit: string, benefitIndex: number) => (
                    <li key={benefitIndex} className="flex items-start gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhoBenefits;